import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { GECKO_UTILS, GeckoAvatar } from 'ui';
import { TooltipIdStr, OverlayTrigger, Tooltip } from 'ui';
import PresenceBadge from 'components/PresenceBadge';
import { fetchContact } from '../../actions';
import { fromNowChathead } from 'utilities/fromNowChathead';

const loadingRing = `${process.env.PUBLIC_URL}/img/loading_ring.svg`;

export class ChatHead extends Component {
  renderClasses() {
    return GECKO_UTILS.classNames({
      'clickable ChatHead d-flex justify-content-between align-items-center': true,
      active: this.props.isActive,
    });
  }

  componentDidMount() {
    if (this.props.contact.placeholder && !this.props.fetchingContact) {
      fetchContact(this.props.user_id);
    }
  }
  componentDidUpdate() {
    if (this.props.contact.placeholder && !this.props.fetchingContact) {
      fetchContact(this.props.user_id);
    }
  }

  changeConversationStatus = (event) => {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    this.props.isClosed
      ? this.props.changeConversationStatus('open', this.props.conversation)
      : this.props.changeConversationStatus('closed', this.props.conversation);
  };

  renderChangeConversationStateButton(tooltipText, icon) {
    return (
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 0, hide: 0 }}
        overlay={
          <Tooltip id={`${TooltipIdStr(this.props.conversation.id)}-icon`}>
            {tooltipText}
          </Tooltip>
        }
      >
        <span
          className="fa-stack fa-lg btn btn-outline-primary icon-only gecko-btn"
          id={`${TooltipIdStr(this.props.conversation.id)}-icon`}
        >
          <i
            className="far fa-stack-2x"
            onClick={this.changeConversationStatus}
          />
          <i
            className={`far fa-${icon} fa-stack-1x`}
            onClick={this.changeConversationStatus}
          />
        </span>
      </OverlayTrigger>
    );
  }

  renderReopenButton() {
    return this.renderChangeConversationStateButton('Reopen', 'unlock');
  }

  renderCloseButton() {
    return this.renderChangeConversationStateButton('Close', 'check');
  }

  renderDeleteButton() {
    return (
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 0, hide: 0 }}
        overlay={
          <Tooltip
            id={`${TooltipIdStr(this.props.conversation.id)}-delete-icon`}
          >
            Delete
          </Tooltip>
        }
      >
        <span
          className="fa-stack fa-lg btn btn-outline-danger icon-only gecko-btn"
          id={`${TooltipIdStr(this.props.conversation.id)}-delete-icon`}
        >
          <i className="far fa-stack-2x" />
          <i
            className="far fa-trash-alt fa-stack-1x"
            onClick={(event) => {
              event.stopPropagation();
              event.nativeEvent.stopImmediatePropagation();
              this.props.deleteConversation(this.props.conversation);
            }}
          />
        </span>
      </OverlayTrigger>
    );
  }

  renderIgnoreButton() {
    return (
      <OverlayTrigger
        placement="bottom"
        delay={{ show: 0, hide: 0 }}
        overlay={
          <Tooltip
            id={`${TooltipIdStr(this.props.conversation.id)}-ignore-icon`}
          >
            Ignore undelivered message
          </Tooltip>
        }
      >
        <span
          className="fa-stack fa-lg btn btn-outline-primary icon-only gecko-btn"
          id={`${TooltipIdStr(this.props.conversation.id)}-ignore-icon`}
        >
          <i className="far fa-stack-2x" />
          <i
            className="far fa-eye-slash fa-stack-1x"
            onClick={(event) => {
              event.stopPropagation();
              event.nativeEvent.stopImmediatePropagation();
              this.props.ignoreConversation(this.props.conversation);
            }}
          />
        </span>
      </OverlayTrigger>
    );
  }

  renderClosedConversationButtons() {
    return (
      <span>
        {this.renderReopenButton()}
        {this.renderDeleteButton()}
      </span>
    );
  }

  renderOpenConversationButtons() {
    return (
      <span>
        {this.props.currentFilter === 'Undelivered'
          ? this.renderIgnoreButton()
          : null}
        {this.renderCloseButton()}
      </span>
    );
  }

  renderChatHeadControls() {
    return (
      <span className="conversation-handler-btns" role="button">
        {this.props.isClosed
          ? this.renderClosedConversationButtons()
          : this.renderOpenConversationButtons()}
      </span>
    );
  }

  renderUserTyping() {
    const users = this.props.typingStatus.filter((u) => u.type === 'user');
    return users.map((user) => {
      return (
        <div key={user.id} className="loading">
          <span>
            {user.first_name ? `${user.first_name} is` : ''} typing...
          </span>
        </div>
      );
    });
  }

  renderAgentTyping() {
    const agents = this.props.typingStatus.filter((u) => u.type === 'agent');
    return agents.map((agent) => {
      return (
        <div key={agent.id} className="loading">
          <span>{agent.full_name} is typing</span>
        </div>
      );
    });
  }

  getLatestMessageFromText(conversation) {
    const { loggedInAgentId, agentFirstName, allAgentIds } = this.props;
    const { last_message_from, bot_has_owned, user_id } = conversation;

    const isFromStudent = last_message_from === user_id;
    const isFromSameAgent = last_message_from === loggedInAgentId;
    const isFromOtherAgent = allAgentIds.includes(last_message_from);
    const isFromBot = bot_has_owned && !isFromStudent && !isFromOtherAgent;

    if (isFromStudent) return '';
    if (isFromSameAgent) return 'You: ';
    if (isFromOtherAgent) return `${agentFirstName}: `;
    if (isFromBot) return 'Bot: ';

    return '';
  }

  renderLatestMessage() {
    const userTyping = this.renderUserTyping();

    if (userTyping.length) {
      return (
        <div className="chat-head-latest-message">
          <em>{userTyping}</em>
        </div>
      );
    }

    const agentTyping = this.renderAgentTyping();

    if (agentTyping.length) {
      return <div className="chat-head-latest-message">{agentTyping}</div>;
    }

    if (this.props.subjectLine) {
      return (
        <div
          id={`${TooltipIdStr(this.props.conversation.id)}-subject-line`}
          className="chat-head-latest-message"
        >
          <span>
            <span className="chat-head-sender-name">Subject:</span>{' '}
            {this.props.subjectLine}
          </span>
        </div>
      );
    }

    if (this.props.conversation.last_message) {
      const latestMessageText = this.getLatestMessageFromText(
        this.props.conversation
      );

      return (
        <div className="chat-head-latest-message">
          <span>
            <span className="chat-head-sender-name">{latestMessageText}</span>
            {this.props.conversation.last_message}
          </span>
        </div>
      );
    }

    return null;
  }

  renderChatHead() {
    const lastMessageTime = this.props.conversation.last_message_time
      ? this.props.conversation.last_message_time
      : this.props.conversation.updatedAt;
    const latestMessage = this.renderLatestMessage();

    return (
      <div
        className={this.renderClasses()}
        onClick={this.props.onClick}
        role="button"
        aria-pressed={this.props.isActive ? 'true' : 'false'}
      >
        {this.renderChatHeadControls()}
        <div className="ChatHead-content d-flex align-items-center">
          <div
            className={`${
              this.props.isActive
                ? 'chat-head-avatar text-center'
                : `chat-head-avatar text-center ${
                    this.props.unreadCount ? 'unread-indicator' : ''
                  }`
            }`}
          >
            <div>
              <PresenceBadge presence={this.props.contact.presence}>
                <GeckoAvatar
                  name={this.props.contact.full_name}
                  first_name={this.props.contact.first_name}
                  last_name={this.props.contact.last_name}
                  color={this.props.contact.additional.avatar_colour}
                  bgImage={this.props.contact.additional.profile_picture_url}
                />
              </PresenceBadge>
            </div>
          </div>
          <div className="chat-head-latest-message-container">
            <div className="chat-head-title">
              {this.props.contact.full_name}
            </div>
            {latestMessage}
          </div>
        </div>
        <div className="chat-head-fromNow-time">
          <a id={`${TooltipIdStr(this.props.conversation.id)}`}>
            {' '}
            {fromNowChathead(lastMessageTime) || 'Never'}{' '}
          </a>
        </div>
      </div>
    );
  }

  render() {
    return <div>{this.renderChatHead()}</div>;
  }
}

ChatHead.defaultProps = {
  lastMessageTimestamp: new Date(),
  lastMessageChannel: null,
  isActive: false,
  isClosed: false,
  unreadCount: 0,
  onClick: () => {},
  changeConversationStatus: () => {},
  conversation: {},
  channels: [],
  contact: {
    additional: {
      profile_picture_url: loadingRing,
    },
    placeholder: true,
  },
};

ChatHead.propTypes = {
  lastMessageTimestamp: PropTypes.string,
  lastMessageChannel: PropTypes.number,
  isActive: PropTypes.bool,
  isClosed: PropTypes.bool,
  unreadCount: PropTypes.number,
  onClick: PropTypes.func,
  changeConversationStatus: PropTypes.func,
  conversation: PropTypes.object,
  contact: PropTypes.object,
  channels: PropTypes.array,
  currentFilter: PropTypes.string,
};

const mapStateToProps = (store, props) => {
  const agent = store.agents.find(
    (a) => a.id === props.conversation.last_message_from
  );

  const allAgentIds = store.agents.map(({ id }) => id);

  return {
    contact: store.conversationsRoute.contactsById[props.user_id],
    fetchingContact:
      store.conversationsRoute.contactsFetchStatus[props.user_id] &&
      store.conversationsRoute.contactsFetchStatus[props.user_id].fetching,
    lastMessageFromAgent: !!agent,
    agentName: agent ? agent.full_name : null,
    agentColor: agent ? agent.additional.avatar_colour : null,
    agentAvatar: agent ? agent.additional.profile_picture_url : null,
    agentFirstName: agent?.first_name ? agent?.first_name : '',
    channels: store.channels,
    typingStatus: store.typing_status[props.conversation.id] || [],
    loggedInAgentId: store.appState.loggedInUser.id,
    allAgentIds: allAgentIds,
  };
};

export default connect(mapStateToProps)(ChatHead);
